import { useState } from 'react';
import type { Screen } from '../../types';
import { NavIcon, Avatar } from '../primitives';

interface Props {
  current: Screen;
  onNav: (screen: Screen) => void;
  previewMode: boolean;
  onTogglePreview: () => void;
}

const TABS: { id: Screen; label: string }[] = [
  { id: 'sources', label: 'Sources' },
  { id: 'stream',  label: 'Data'    },
  { id: 'ask',     label: 'Ask'     },
];

export function TopBar({ current, onNav, previewMode, onTogglePreview }: Props) {
  const [query, setQuery] = useState('');

  return (
    <header
      className="topbar"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '20px 32px 18px',
        borderBottom: '1px solid var(--line-soft)',
        background: 'var(--surface)',
        position: 'sticky',
        top: 0,
        zIndex: 5,
      }}
    >
      {/* Pill nav */}
      <div
        className="nav-pill-container"
        style={{
          display: 'inline-flex',
          gap: 6,
          background: 'var(--white)',
          padding: 4,
          borderRadius: 'var(--radius-full)',
          border: '1px solid var(--line)',
        }}
      >
        {TABS.map((t) => {
          const active = current === t.id;
          return (
            <button
              key={t.id}
              onClick={() => onNav(t.id)}
              className={active ? 'nav-tab-active' : ''}
              style={{
                border: 'none',
                background: active ? 'var(--deep)' : 'transparent',
                color: active ? 'white' : 'var(--slate)',
                padding: '0 16px',
                height: 32,
                borderRadius: 'var(--radius-full)',
                fontFamily: 'inherit',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all .15s',
                letterSpacing: '-0.005em',
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div
        style={{
          flex: 1,
          maxWidth: 540,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          height: 40,
          padding: '0 14px',
          background: 'var(--white)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--radius-full)',
        }}
      >
        <NavIcon name="search" size={16} stroke="var(--slate-light)" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Ask Brain anything, or search across all sources…"
          style={{
            flex: 1,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            fontFamily: 'inherit',
            fontSize: 13,
            color: 'var(--deep)',
          }}
        />
        <span
          style={{
            padding: '2px 6px',
            borderRadius: 4,
            background: 'var(--surface-2)',
            color: 'var(--slate)',
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            fontWeight: 600,
          }}
        >
          ⌘K
        </span>
      </div>

      {/* Preview mode toggle */}
      <button
        title="Preview what others see"
        onClick={onTogglePreview}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          height: 40,
          padding: '0 14px',
          borderRadius: 'var(--radius-full)',
          background: previewMode ? 'rgba(217,119,6,.10)' : 'var(--white)',
          border: previewMode ? '1px dashed var(--priv-team)' : '1px solid var(--line)',
          color: previewMode ? 'var(--priv-team)' : 'var(--slate)',
          cursor: 'pointer',
          fontSize: 12,
          fontWeight: 600,
          fontFamily: 'inherit',
        }}
      >
        <NavIcon name="eye" size={14} />
        {previewMode ? 'Showing company view' : 'Preview as others'}
      </button>

      {/* Notifications */}
      <button
        title="Notifications"
        aria-label="Notifications"
        style={{
          width: 40,
          height: 40,
          borderRadius: 'var(--radius-full)',
          background: 'var(--white)',
          border: '1px solid var(--line)',
          color: 'var(--slate)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          position: 'relative',
          fontFamily: 'inherit',
        }}
      >
        <NavIcon name="bell" size={16} />
        <span
          style={{
            position: 'absolute',
            top: 8,
            right: 9,
            width: 7,
            height: 7,
            background: 'var(--priv-team)',
            borderRadius: 4,
            border: '2px solid white',
            boxSizing: 'content-box',
          }}
        />
      </button>

      {/* Profile */}
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, paddingLeft: 6 }}>
        <div style={{ textAlign: 'right', lineHeight: 1.15 }}>
          <div style={{ fontSize: 13, fontWeight: 600, letterSpacing: '-0.01em' }}>AB</div>
          <div style={{ fontSize: 11, color: 'var(--slate)', fontFamily: 'var(--font-mono)' }}>@fairmint</div>
        </div>
        <Avatar initials="AB" color="#2D00F7" size={38} />
      </div>
    </header>
  );
}
